import { useRef, useState, type FormEvent } from 'react';
import type { CreateExpenseInput, Expense } from '@dsr/shared-types';
import {
  downloadReceipt,
  useApproveExpense,
  useCreateExpense,
  useDeleteExpense,
  useExpenses,
  useLookup,
  useProjects,
  useReimburseExpense,
  useUploadReceipt,
  useVendors,
  type ExpenseFilters,
} from '../api/queries/financials';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { fmtMoney } from '../utils/money';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Select,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';

const MONEY_RE = /^\d+(\.\d{1,2})?$/;

export function ExpensesPage(): JSX.Element {
  const [filters, setFilters] = useState<ExpenseFilters>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [removing, setRemoving] = useState<Expense | null>(null);

  const { data, isLoading, isError } = useExpenses(filters);
  const { data: types } = useLookup('expense_type');
  const { data: projectsData } = useProjects({});
  const approve = useApproveExpense();
  const reimburse = useReimburseExpense();
  const del = useDeleteExpense();
  const upload = useUploadReceipt();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<number | null>(null);

  const canWrite = can('expense.write');
  const canApprove = can('expense.approve');
  const canDelete = can('expense.delete');
  const expenses = data?.expenses ?? [];

  const totalShown = expenses.reduce((sum, e) => sum + Number(e.amount), 0);

  const onAction = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Action failed.');
    }
  };

  const onPickReceipt = (expenseId: number) => {
    setUploadTarget(expenseId);
    fileRef.current?.click();
  };

  const onFileChosen = async (file: File | undefined) => {
    if (!file || uploadTarget === null) return;
    await onAction(
      () => upload.mutateAsync({ id: uploadTarget, file }),
      'Receipt attached.',
    );
    setUploadTarget(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Project filter: Radix Select forbids an empty value, so the all-projects
  // sentinel is the literal 'all'; 'none' = general business (no project).
  const projectFilterValue =
    filters.project_id === undefined ? 'all' : String(filters.project_id);
  const projectFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All projects' },
    { value: 'none', label: 'General business (no project)' },
    ...(projectsData?.projects ?? []).map((p) => ({
      value: String(p.id),
      label: p.title,
    })),
  ];

  // Type filter: 'all' sentinel stands in for the empty "all types" option.
  const typeFilterValue = filters.expense_type ?? 'all';
  const typeFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All types' },
    ...(types?.options ?? []).map((o) => ({ value: o.value, label: o.label })),
  ];

  return (
    <div className="page expenses-page">
      <header className="page-header">
        <div>
          <h1>Expenses</h1>
          <p className="page-sub">
            {expenses.length > 0
              ? `${expenses.length} entries shown · ${fmtMoney(String(totalShown.toFixed(2)))}`
              : 'Money out — project costs and general business expenses.'}
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Log expense
          </Button>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={projectFilterValue}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              project_id: v === 'all' ? undefined : v === 'none' ? 'none' : Number(v),
            }))
          }
          options={projectFilterOptions}
          aria-label="Filter by project"
        />

        <Select
          value={typeFilterValue}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, expense_type: v === 'all' ? undefined : v }))
          }
          options={typeFilterOptions}
          aria-label="Filter by type"
        />

        <label className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={filters.reimbursable === 'pending'}
            onChange={(e) =>
              setFilters((f) => ({ ...f, reimbursable: e.target.checked ? 'pending' : undefined }))
            }
          />
          <span>Awaiting reimbursement</span>
        </label>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && <p className="error-text" role="alert">Failed to load expenses.</p>}
      {!isLoading && !isError && expenses.length === 0 && (
        <p className="muted">No expenses match the current filters.</p>
      )}

      {expenses.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Vendor</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell aria-label="Actions" />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {expenses.map((e) => (
              <Table.Row key={e.id}>
                <Table.Cell>{fmtDate(e.expense_date)}</Table.Cell>
                <Table.Cell>
                  {e.expense_type}
                  {e.reimbursable && <div className="muted small">reimbursable</div>}
                </Table.Cell>
                <Table.Cell>{e.project_title ?? 'General'}</Table.Cell>
                <Table.Cell tone="muted">{e.vendor_name ?? '—'}</Table.Cell>
                <Table.Cell>
                  {fmtMoney(e.amount)}
                  {Number(e.tax_amount) > 0 && (
                    <div className="muted small">incl. tax {fmtMoney(e.tax_amount)}</div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {e.reimbursed_at ? 'Reimbursed' : e.approved_at ? 'Approved' : 'Entered'}
                </Table.Cell>
                <Table.Cell>
                  <div className="settings-actions">
                    {e.receipt_path ? (
                      <Button
                        variant="ghost"
                        onClick={() => void downloadReceipt(e.id)}
                        title="Download receipt"
                      >
                        Receipt
                      </Button>
                    ) : canWrite ? (
                      <Button
                        variant="ghost"
                        onClick={() => onPickReceipt(e.id)}
                        disabled={upload.isPending}
                      >
                        + Receipt
                      </Button>
                    ) : null}
                    {canApprove && !e.approved_at && (
                      <Button
                        variant="ghost"
                        onClick={() => void onAction(() => approve.mutateAsync(e.id), 'Expense approved.')}
                      >
                        Approve
                      </Button>
                    )}
                    {canApprove && e.reimbursable && e.approved_at && !e.reimbursed_at && (
                      <Button
                        variant="ghost"
                        onClick={() => void onAction(() => reimburse.mutateAsync(e.id), 'Marked reimbursed.')}
                      >
                        Reimburse
                      </Button>
                    )}
                    {canDelete && !e.approved_at && (
                      <Button variant="ghost" onClick={() => setRemoving(e)}>
                        Remove
                      </Button>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Hidden file input shared by every "+ Receipt" button. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf"
        style={{ display: 'none' }}
        onChange={(e) => void onFileChosen(e.target.files?.[0])}
      />

      {createOpen && <CreateExpenseModal onClose={() => setCreateOpen(false)} />}
      {removing && (
        <ConfirmDialog
          open
          title="Remove expense"
          message={`Remove the ${fmtMoney(removing.amount)} ${removing.expense_type} expense from ${fmtDate(removing.expense_date)}?`}
          confirmLabel="Remove"
          danger
          busy={del.isPending}
          onConfirm={() =>
            void onAction(() => del.mutateAsync(removing.id), 'Expense removed.').then(() =>
              setRemoving(null),
            )
          }
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

function CreateExpenseModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [type, setType] = useState('');
  const [amount, setAmount] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [date, setDate] = useState<number | null>(Date.now());
  const [vendorId, setVendorId] = useState<number | ''>('');
  const [taxCategory, setTaxCategory] = useState('');
  const [reimbursable, setReimbursable] = useState(false);
  const [mileage, setMileage] = useState('');
  const [notes, setNotes] = useState('');

  const { data: types } = useLookup('expense_type');
  const { data: taxCats } = useLookup('tax_category');
  const { data: projectsData } = useProjects({});
  const { data: vendorsData } = useVendors();
  const create = useCreateExpense();

  const vendors = vendorsData?.vendors ?? [];

  // Vendor pick pre-fills its default tax category (user can still override).
  const onVendorChange = (raw: string) => {
    const id = raw === '' ? '' : Number(raw);
    setVendorId(id);
    if (id !== '') {
      const v = vendors.find((x) => x.id === id);
      if (v?.default_tax_category && !taxCategory) setTaxCategory(v.default_tax_category);
    }
  };

  const validMoney = MONEY_RE.test(amount)
    && (taxAmount === '' || MONEY_RE.test(taxAmount))
    && (mileage === '' || MONEY_RE.test(mileage));
  const taxOk = taxAmount === '' || Number(taxAmount) <= Number(amount);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!date || !type || !validMoney || !taxOk) return;
    const input: CreateExpenseInput = {
      expense_type: type,
      amount,
      expense_date: date,
    };
    if (projectId !== '') input.project_id = projectId;
    if (taxAmount) input.tax_amount = taxAmount;
    if (vendorId !== '') input.vendor_id = vendorId;
    if (taxCategory) input.tax_category = taxCategory;
    if (reimbursable) input.reimbursable = true;
    if (mileage) input.mileage = mileage;
    if (notes.trim()) input.notes = notes.trim();

    try {
      await create.mutateAsync(input);
      toast.success('Expense logged.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to log expense.');
    }
  };

  return (
    <Dialog
      title="Log expense"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-expense-form"
            variant="primary"
            disabled={create.isPending || !type || !date || !validMoney || !taxOk}
          >
            {create.isPending ? 'Saving…' : 'Log expense'}
          </Button>
        </>
      }
    >
      <form id="create-expense-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Project">
          <select
            className="form-input"
            value={projectId === '' ? '' : String(projectId)}
            onChange={(e) => setProjectId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">General business (no project)</option>
            {(projectsData?.projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Type" required>
          <select className="form-input" value={type} onChange={(e) => setType(e.target.value)} required>
            <option value="">Select…</option>
            {(types?.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label={`Amount * ${taxOk ? '' : '(tax exceeds amount)'}`}>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="125.00"
            inputMode="decimal"
            required
          />
        </FormField>
        <FormField label="Tax included">
          <Input
            value={taxAmount}
            onChange={(e) => setTaxAmount(e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
          />
        </FormField>
        <FormField label="Date" required>
          <DateInput value={date} onChange={setDate} required />
        </FormField>
        <FormField label="Vendor">
          <select
            className="form-input"
            value={vendorId === '' ? '' : String(vendorId)}
            onChange={(e) => onVendorChange(e.target.value)}
          >
            <option value="">None</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.dba_name ?? v.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Tax category">
          <select className="form-input" value={taxCategory} onChange={(e) => setTaxCategory(e.target.value)}>
            <option value="">Uncategorized</option>
            {(taxCats?.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Mileage">
          <Input
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            placeholder="42.5"
            inputMode="decimal"
          />
        </FormField>
        <label className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={reimbursable}
            onChange={(e) => setReimbursable(e.target.checked)}
          />
          <span>Reimbursable (paid personally, owed back)</span>
        </label>
        <FormField label="Notes" full>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </form>
    </Dialog>
  );
}
